import { FormControl, FormLabel, GridItem, Stack } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import gql from 'graphql-tag';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~auth/useAuth';
import { DisplayDefaultContentSource } from '~components/displays/DisplayDetail/playback/DisplayDefaultContentSource';
import { DisplayLedStripColorSelect } from '~components/displays/DisplayDetail/playback/DisplayLedStripColorSelect';
import { DisplayOrientationToggle } from '~components/displays/DisplayDetail/playback/DisplayOrientationToggle';
import {
  isDefaultContentSourcesSupported as isDefaultContentSourcesSupportedByDisplay,
  isHostControlVisible,
} from '~components/displays/utils';
import {
  FormLabelPendingIndicator,
  isPropertyPending,
} from '~components/ui/FormLabelPendingIndicator';
import { PlaybackSection_DisplayFragment } from './__generated__/PlaybackSection.graphql';
import { DisplayBrightnessSlider } from './DisplayBrightnessSlider';
import { DisplayBrightnessV2Slider } from './DisplayBrightnessV2Slider';
import { DisplayVolumeLimits } from './DisplayVolumeLimits';

interface Props {
  display: PlaybackSection_DisplayFragment;
}

export function PlaybackSection({ display }: Props) {
  const isDefaultContentSourcesSupported = isDefaultContentSourcesSupportedByDisplay(display);
  const { verifyUserPermissions } = useAuth();

  const hasDisplaySettingsUpdatePermission = verifyUserPermissions([
    Permission.DisplaySettingsUpdate,
  ]);
  const { t } = useTranslation();

  const isBrightnessVisible = isHostControlVisible(display, 'brightness');
  return (
    <>
      <GridItem colSpan={{ base: 8, md: 7 }}>
        <Stack direction="column" spacing="10">
          {isDefaultContentSourcesSupported && <DisplayDefaultContentSource display={display} />}

          <DisplayOrientationToggle display={display} />
        </Stack>
      </GridItem>

      {isBrightnessVisible && isNil(display.brightnessV2) && !isNil(display.brightness) && (
        <GridItem colSpan={{ base: 8, md: 7 }} mt={10} maxWidth="md">
          <FormControl>
            <FormLabel>
              {t('brightness')}
              <FormLabelPendingIndicator isPending={isPropertyPending(display.brightness)} />
            </FormLabel>
            <DisplayBrightnessSlider
              display={display}
              isDisabled={!hasDisplaySettingsUpdatePermission}
            />
          </FormControl>
        </GridItem>
      )}

      {isNil(display.brightness) && !isNil(display.brightnessV2) && (
        <GridItem colSpan={{ base: 8, md: 7 }} mt={10} maxWidth="md">
          <FormControl>
            <FormLabel>
              {t('brightness')}
              <FormLabelPendingIndicator isPending={isPropertyPending(display.brightnessV2)} />
            </FormLabel>
            <DisplayBrightnessV2Slider
              display={display}
              isDisabled={!hasDisplaySettingsUpdatePermission}
            />
          </FormControl>
        </GridItem>
      )}

      {display.ledStripColor !== null && (
        <GridItem colSpan={{ base: 8, sm: 4, lg: 8, xl: 3 }} mt={10}>
          <FormControl>
            <FormLabel>
              {t('ledStrip')}
              <FormLabelPendingIndicator isPending={isPropertyPending(display.ledStripColor)} />
            </FormLabel>
            <DisplayLedStripColorSelect
              display={display}
              isDisabled={!hasDisplaySettingsUpdatePermission}
            />
          </FormControl>
        </GridItem>
      )}

      <DisplayVolumeLimits display={display} />
    </>
  );
}

PlaybackSection.graphql = {
  fragments: {
    PlaybackSection_display: gql`
      fragment PlaybackSection_display on Display {
        id
        host {
          coupledState
        }
        target {
          brightness
        }
        ...DisplayVolumeLimits_display
        ...DisplayDefaultContentSource_display
        ...DisplayOrientationToggle_display
        ...DisplayLedStripColorSelect_display
        ...DisplayBrightnessSlider_display
        ...DisplayBrightnessV2Slider_display
      }
    `,
  },
};
